'use client';

import pathlib from 'path';

import * as ENGINE from '@gnsx/genesys.js';
import { PROJECT_PATH_PREFIX } from '@gnsx/genesys.js';
import CRC32 from 'crc-32';
import { flow, getSnapshot, t } from 'mobx-state-tree';

import { editorAssets } from './editorStore';

import type { IStorageProvider } from '@/lib/storage';
import type { Instance } from 'mobx-state-tree';

import { sdk, type ProjectInfo as SdkProjectInfo, type ToolCallingResult } from '@/app/sdk';
import { resetAppliedBuildStamp } from '@/editor/core/project-file-watch/buildStampState';
import {
  clearBuildStampRefreshHandler,
  clearPendingDirectoryRefreshes,
  createFileChangeHandler,
} from '@/editor/core/project-file-watch/fileWatcherHandler';
import { storageFactory, StorageProviderType } from '@/lib/storage';
import { IS_DEV } from '@/lib/utils';

const DEBUG_LOG = {
  directories: IS_DEV,
  fileSystemChange: IS_DEV,
} as const;

const PROJECT_NAME = 'editor-world';

export const Project = t.model('Project', {
  currentScene: t.maybeNull(t.string),
  project: t.maybeNull(t.string),
  projectFolder: t.optional(t.string, ''),  // NOTE: this is an absolute path, as in `D:\path\to\project`, as it's from sdk
  projectFile: t.optional(t.string, ''),
  availableProjects: t.array(t.model('ProjectInfo', {
    folder: t.string,
    file: t.string,
    projectName: t.string,
    lastOpened: t.number,
    isFound: t.boolean,
  })),
}).volatile(() => ({
  currentSceneCrc32: null as number | null,
  unsubscribeFileSystemChange: null as any,
})).views(self => ({
  // keep this for now, as it might be from a saved project history, but we should not use it as
  // sdk now make sure only posix style paths are passed here
  get normalizedProjectPath() { return self.projectFolder.replace(/\\/g, '/'); },
  get projectDir() { return this.normalizedProjectPath; },
  get projectPath() {
    return self.projectFolder ? pathlib.join(this.normalizedProjectPath, self.projectFile) : '';
  },
  get projectName() { return pathlib.basename(self.projectFile, ENGINE.GAME_PROJECT_FILE_EXT); },
  get sceneName() { return pathlib.basename(self.currentScene ?? '', ENGINE.SCENE_NAME_EXT); },
  get projectFilePathWithProjectPrefix() { return pathlib.join(PROJECT_PATH_PREFIX, self.projectFile); },
  get isProjectSelected() { return !!self.projectFolder; },
  get isProjectOpened() { return !!self.project; },
  makeScopedStorageProvider(): IStorageProvider {
    // TODO: a temp hack due to `IStorageProvider` exist in both engine and genesys.ai. There will only be the engine version
    if (ENGINE.projectInfo.project !== PROJECT_NAME) {
      throw new Error('Project has not been opened yet!');
    }
    return ENGINE.projectInfo.storageProvider! as IStorageProvider;
  }
})).actions(self => {
  const setProjectPath = (projectFolder: string, projectFile: string) => {
    console.log('[Project.setProjectPath] projectFolder:', projectFolder, 'projectFile:', projectFile);
    if (self.projectFolder === projectFolder && self.projectFile === projectFile) {
      return;
    }
    self.projectFolder = projectFolder;
    self.projectFile = projectFile;
    self.currentScene = null;
    self.currentSceneCrc32 = null;
  };
  return {
    setProjectPath,
    loadProject: flow(function* _loadProject(projectFolder: string, projectFile: string, buildProject: boolean = true): Generator<Promise<any>, void, any> {
      console.log(`[Project.loadProject] Load project at ${projectFolder}, with file ${projectFile}, buildProject: ${buildProject}`);
      setProjectPath(projectFolder, projectFile);
      ENGINE.projectInfo?.resetProject();
      const storageProvider = storageFactory.getScopedStorageProvider(StorageProviderType.FILE_SERVER);
      ENGINE.projectContext({ project: PROJECT_NAME, storageProvider: storageProvider });
      self.project = PROJECT_NAME;
      if (sdk && buildProject) {
        const buildResult: ToolCallingResult = yield sdk.tools.buildProject(projectFolder);
        if (!buildResult.success) {
          console.warn('[Project.loadProject] Failed to build project:', buildResult.error || buildResult.message);
        } else {
          console.log('[Project.loadProject] Project built successfully:', buildResult.message);
        }
      }
      yield editorAssets.refreshClassInfos();
      yield editorAssets.assetTree.refreshAllDirectories(true, true);
      editorAssets.assetTree.resetThumbnailSession();

      editorAssets.setCurrentDirectory(`${PROJECT_PATH_PREFIX}/assets`, false, false, 'replace');
      if (DEBUG_LOG.directories) {
        console.log('[Project.loadProject] folders:', getSnapshot(editorAssets.assetTree.folders));
        console.log('[Project.loadProject] files:', getSnapshot(editorAssets.assetTree.files));
        console.log('[Project.loadProject] folderTree:', editorAssets.assetTree.directoryTree);
        console.log('[Project.loadProject] currentFolder:', editorAssets.assetTree.currentFolder?.path);
      }
      self.unsubscribeFileSystemChange = subscribeFileSystemChange(self as IProject);
    }),
    setCurrentScene(scenePath: string | null, sceneContent: string | null) {
      self.currentScene = scenePath;
      console.log('[Project.setCurrentScene] scenePath:', scenePath);
      self.currentSceneCrc32 = sceneContent ? CRC32.str(sceneContent) : null;
    },
    refreshProjects: flow(function* _refreshProjects(): Generator<Promise<any>, void, any> {
      if (!sdk) {
        console.warn('🚫 SDK not available, cannot refresh projects');
        return;
      }
      try {
        console.log('🔄 Refreshing available projects from SDK...');
        const projects = yield sdk.tools.getProjects();
        // Clear existing projects and add new ones
        self.availableProjects.clear();
        projects.forEach((projectInfo: SdkProjectInfo) => {
          const folder = projectInfo.folder.replace(/\\/g, '/');
          let projectName = pathlib.basename(projectInfo.file, ENGINE.GAME_PROJECT_FILE_EXT);
          if (!projectName) {
            projectName = pathlib.basename(folder);
          }
          self.availableProjects.push({
            folder,
            file: projectInfo.file,
            projectName: projectName,
            lastOpened: projectInfo.lastOpened,
            isFound: projectInfo.isFound,
          });
        });
        console.log(`✅ Successfully refreshed ${projects.length} projects`, projects);
      } catch (error) {
        console.error('❌ Failed to refresh projects:', error);
      }
    }),
    /** Invokes stored `unsubscribeFileSystemChange` callback and clears it (e.g. when leaving the editor route). */
    teardownFileSystemChangeListener() {
      teardownProjectFileSystemListener(self as IProject);
    },
    /**
     * Marks the project as closed while retaining folder/file paths for reopen.
     * File-server stop, play-mode exit, and thumbnail cleanup are handled by {@link closeProject}.
     */
    markProjectClosed() {
      if (!self.project) {
        return;
      }
      console.log('[Project.markProjectClosed] projectFolder:', self.projectFolder);
      teardownProjectFileSystemListener(self as IProject);
      try {
        ENGINE.projectInfo?.resetProject();
      } catch (error) {
        console.warn('[Project.markProjectClosed] Failed to reset engine project context:', error);
      }
      self.project = null;
    },
  };
});

export interface IProject extends Instance<typeof Project> { }

function teardownProjectFileSystemListener(projectModel: IProject): void {
  projectModel.unsubscribeFileSystemChange?.();
  projectModel.unsubscribeFileSystemChange = null;
  clearPendingDirectoryRefreshes();
  clearBuildStampRefreshHandler();
  resetAppliedBuildStamp();
}

function subscribeFileSystemChange(project: IProject) {
  project.unsubscribeFileSystemChange?.();
  let unsubscribe: (() => void) | null = null;
  try {
    const storageProvider = project.makeScopedStorageProvider();
    const onChange = createFileChangeHandler(project);
    console.log('[Project.subscribeFileSystemChange] subscribe');
    unsubscribe = storageProvider.onFileSystemChange((event) => {
      if (DEBUG_LOG.fileSystemChange) {
        const changePath = ENGINE.AssetPath.fromString(ENGINE.AssetPath.join(PROJECT_PATH_PREFIX, event.path));
        console.debug('[Project.subscribeFileSystemChange] detail:', { path: changePath.initialPath, event });
      }

      onChange(event);
    });
  } catch (error) {
    console.warn('[Project.subscribeFileSystemChange] Failed to subscribe:', error);
  }
  return unsubscribe;
}

